import React, { useEffect } from "react";

import useMeasure from "react-use-measure";
import { useRef } from "react";
import { nanoid } from "nanoid";
import { Cropper, ReactCropperElement } from "react-cropper";
import "cropperjs/dist/cropper.css";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import Slider from "src/components/slider";
import sliderLayoutStyles from "src/components/slider/slider.module.scss";
import Button from "src/components/button";
import Thumbnail from "src/components/thumbnail";
import Tooltip from "src/components/tooltip";
import { MediaLibraryChooser } from "src/components/mediaLibrary";
import { POSITIONS } from "src/constants/banner";

import { Banner } from "src/types/common";
import { Media, MediaFile } from "src/types/media";
import { MediaLibrary } from "src/types/mediaLibrary";

import { parseMediaMetadata } from "src/utils/media.service";

import { invalidateMediaUpload, uploadMedia } from "src/redux/actions/media";

import _ from "lodash";
import cx from "classnames";
import styles from "./rewardThumbnailPicker.module.scss";
import { animated, useTransition } from "@react-spring/web";
import Actions from "src/components/actionbar";

const DEFAULT_REWARD_BANNER = "/images/collections/background.jpeg";

export interface RewardThumbnailPickerProps {
  show: boolean;
  onClose: () => void;
  onFinish: (banner: Banner) => void;
}

export const RewardThumbnailPicker: React.FC<RewardThumbnailPickerProps> = ({
  show,
  onClose,
  onFinish,
}) => {
  const dispatch = useDispatch();
  const [editAction, setEditAction] = React.useState<"edit" | "crop" | "position" | null>("edit");
  const [bannerState, setBannerState] = React.useState<Banner | null>(null);

  const uploading = useSelector((state: any) => {
    const background = _.get(bannerState, "background");
    const entityType = _.get(background, "entityType");
    if (background && entityType === "media-preview") {
      return _.get(state, `media['${background.id}'].loading`, false);
    }
    return false;
  });

  const uploaded = useSelector((state: any) => {
    const background = _.get(bannerState, "background");
    const entityType = _.get(background, "entityType");
    if (background && entityType === "media-preview") {
      return _.get(state, `media['${background.id}'].loaded`, false);
    }
    return false;
  });

  const uploadedBackground = useSelector((state: any) => {
    const background = _.get(bannerState, "background");
    const entityType = _.get(background, "entityType");
    if (background && entityType === "media-preview") {
      return _.get(state, `media['${background.id}'].item`);
    }
    return null;
  });

  useEffect(() => {
    if (!show) return;
    setEditAction("edit");
    setBannerState(null);
  }, [show]);

  useEffect(() => {
    if (uploaded && uploadedBackground) {
      onFinish({ ...bannerState!, background: uploadedBackground });
      const mediaId = _.get(bannerState, "background.id");
      if (mediaId) dispatch(invalidateMediaUpload(mediaId));
      onClose();
    }
  }, [uploaded, uploadedBackground]);

  const handleBannerChange = (background: MediaFile) => {
    setBannerState({
      position: "top",
      autoHeightEnabled: true,
      sharedByEnabled: true,
      crop: null,
      bannerHeight: 0,
      filter: null,
      overlayColor: null,
      fontColor: null,
      descriptionOverlay: null,
      background,
    });
    setEditAction(null);
  };

  const handleSave = async () => {
    if (editAction) {
      setEditAction(null);
      return;
    }
    const media = _.get(bannerState, "background");
    const entityType = _.get(media, "entityType");
    if (media && entityType === "media-preview") {
      const fullUrl = _.get(media, "full");
      const blob = fullUrl ? await fetch(fullUrl).then((r) => r.blob()) : null;
      if (!blob) return;
      const file = new File([blob], "banner.png", {
        type: (media as MediaFile).contentType || blob.type,
      });
      dispatch(uploadMedia({ id: _.get(media, "id"), media: { ...media, file } as MediaFile }));
    } else if (bannerState) {
      onFinish(bannerState);
      onClose();
    }
  };

  const dialogWidth = editAction === "edit" ? "42rem" : "400px";

  return (
    <Slider.Dialog
      className={cx(sliderLayoutStyles.slider, styles.dialog)}
      width={dialogWidth}
      show={show}
      onClose={onClose}
      placement="start"
    >
      <div className={styles.rewardThumbnailPicker}>
        {editAction !== "edit" && bannerState && (
          <PreviewThumbnail
            banner={bannerState}
            editAction={editAction}
            uploading={uploading}
            onActionChange={(action) => setEditAction(action as "edit" | "crop" | "position")}
            onSave={handleSave}
            onClose={onClose}
            onCropChange={(crop) => setBannerState((prev) => prev ? { ...prev, crop } : prev)}
            onPositionChange={(position) => setBannerState((prev) => prev ? { ...prev, position } : prev)}
          />
        )}

        {editAction === "edit" && (
          <ChooseMedia onChange={handleBannerChange} onClose={onClose} />
        )}
      </div>
    </Slider.Dialog>
  );
};


interface PreviewThumbnailProps {
  banner: Banner;
  editAction: "crop" | "position" | null;
  uploading: boolean;
  onActionChange: (action: string) => void;
  onSave: () => void;
  onClose: () => void;
  onCropChange: (crop: { x: number; y: number; width: number; height: number }) => void;
  onPositionChange: (position: string) => void;
}

const PreviewThumbnail: React.FC<PreviewThumbnailProps> = ({
  banner,
  editAction,
  uploading,
  onActionChange,
  onSave,
  onClose,
  onCropChange,
  onPositionChange,
}) => {
  return (
    <div className={styles.customize}>
      <div className={styles.customizeHeader}>
        <button
          type="button"
          className={styles.backButton}
          onClick={() => editAction ? onActionChange(null as any) : onActionChange("edit")}
          aria-label="Back"
        >
          <i className="paperflite-left-arrow" />
        </button>
        <div className={styles.customizeTitles}>
          <h2 className={styles.customizeTitle}>customize your image</h2>
          <p className={styles.customizeSubtitle}>
            pick your preferred layout to match your reward card.
          </p>
        </div>
      </div>

      <div className={styles.previewBody}>
        {editAction === "crop" && (
          <ThumbnailPickerCrop banner={banner} onCropChange={onCropChange} />
        )}

        {editAction !== "crop" && (
          <div className={styles.previewCenter}>
            <Thumbnail
              media={banner.background}
              className={styles.rewardThumbnail}
              crop={banner?.crop}
              position={banner.position}
              alt=""
              defaultImage={DEFAULT_REWARD_BANNER}
            />
            {editAction === "position" ? (
              <BannerPositionMenu handlePositionChange={onPositionChange} />
            ) : (
              <ThumbnailPickerToolbar onActionChange={onActionChange} />
            )}
          </div>
        )}
      </div>

      <div className={styles.actions}>
        <Button
          variant="primary"
          icon="paperflite-tick"
          text={uploading ? "Uploading..." : "Confirm"}
          disabled={uploading}
          onClick={onSave}
        />
        <Button variant="secondary" text="Cancel" disabled={uploading} onClick={onClose} />
      </div>
    </div>
  );
};


interface ChooseMediaProps {
  onChange: (background: MediaFile) => void;
  onClose: () => void;
}

const ChooseMedia: React.FC<ChooseMediaProps> = ({ onChange, onClose }) => {
  const handleLibrarySelect = async (media: MediaLibrary) => {
    try {
      const imageUrl = media.imageUrl;
      if (!imageUrl) return;
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      onChange({
        id: nanoid(6),
        entityType: "media-preview",
        type: media.mediaType,
        full: url,
        thumbnail: url,
        width: media.width,
        height: media.height,
        contentType: blob.type || "image/jpeg",
      } as MediaFile);
    } catch {
      toast.error("Could not load image");
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = _.get(e.target, "files[0]") as File | undefined;
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Please upload a file of size less than 10MB");
      return;
    }
    const background = (await parseMediaMetadata(file)) as MediaFile;
    onChange(background);
  };

  return (
    <div className={styles.pick}>
      <div className={styles.pickHeader}>
        <button
          type="button"
          className={styles.backButton}
          onClick={onClose}
          aria-label="Close"
        >
          <i className="paperflite-left-arrow" />
        </button>
        <div className={styles.pickTitles}>
          <h2 className={styles.pickTitle}>pick your thumbnail image</h2>
          <p className={styles.pickSubtitle}>
            Choose from your library or upload a new image.
          </p>
        </div>
        <button type="button" className={styles.uploadButton}>
          <i className="paperflite-upload" aria-hidden />
          Upload
          <input type="file" accept="image/*" title="" onChange={handleFileUpload} />
        </button>
      </div>
      <div className={styles.mediaLibrary}>
        <MediaLibraryChooser entityType="collection" columns={3} onChange={handleLibrarySelect} allowedMediaType="image" />
      </div>
    </div>
  );
};


interface ThumbnailPickerCropProps {
  banner: Banner;
  onCropChange: (crop: { x: number; y: number; width: number; height: number }) => void;
}

const ThumbnailPickerCrop: React.FC<ThumbnailPickerCropProps> = ({ banner, onCropChange }) => {
  const bannerImage = _.get(banner, "background.full");
  const [editorRef, editorBounds] = useMeasure();
  const cropperRef = useRef<ReactCropperElement>(null);
  const initialCrop = _.get(banner, "crop") || null;

  return (
    <div ref={editorRef} className={styles.cropBanner}>
      <Cropper
        ref={cropperRef}
        src={bannerImage}
        style={{ maxWidth: "100%", maxHeight: "100%" }}
        zoomable={false}
        viewMode={1}
        guides={false}
        minContainerHeight={editorBounds.height}
        ready={() => {
          if (initialCrop && cropperRef.current) {
            cropperRef.current.cropper.setData(initialCrop);
          }
        }}
        data={initialCrop ? {
          x: initialCrop.x || 0,
          y: initialCrop.y || 0,
          width: initialCrop.width || 100,
          height: initialCrop.height || 100,
        } : undefined}
        autoCrop
        autoCropArea={1}
        crop={() => {
          if (cropperRef.current) {
            onCropChange(cropperRef.current.cropper.getData());
          }
        }}
      />
    </div>
  );
};


interface ThumbnailPickerToolbarProps {
  onActionChange: (action: string) => void;
}

const ThumbnailPickerToolbar: React.FC<ThumbnailPickerToolbarProps> = ({ onActionChange }) => {
  return (
    <div className={styles.toolbar}>
      <Tooltip content="Pick another image" placement="top">
        <div className={styles.toolItem} onClick={() => onActionChange("edit")} role="presentation">
          <i className={cx(styles.toolIcon, "paperflite-pen")} />
        </div>
      </Tooltip>
      <Tooltip content="Crop" placement="top">
        <div className={styles.toolItem} onClick={() => onActionChange("crop")} role="presentation">
          <i className={cx(styles.toolIcon, "paperflite-crop")} />
        </div>
      </Tooltip>
      <Tooltip content="Position" placement="top">
        <div className={styles.toolItem} onClick={() => onActionChange("position")} role="presentation">
          <i className={cx(styles.toolIcon, "paperflite-focus")} />
        </div>
      </Tooltip>
    </div>
  );
};

interface BannerPositionMenuProps {
  handlePositionChange: (value: string) => void;
}

const BannerPositionMenu: React.FC<BannerPositionMenuProps> = ({ handlePositionChange }) => {

  const transitions = useTransition(true, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 500 },
    delay: 500,
  });

  return transitions((style, item) =>
    item ? (
      <animated.div style={style} className={styles.positionMenu}>
        <Actions.Container>
          <Actions.Row className={styles.positionActionRow}>
            <Actions.Item className={styles.positions}>
              {POSITIONS.map((position) => {
                return (
                  <div
                    key={position.value}
                    className={cx(styles.position)}
                    onClick={() => handlePositionChange(position.value)}
                  />
                );
              })}
            </Actions.Item>
          </Actions.Row>
        </Actions.Container>
      </animated.div>
    ) : null
  );
};